// src/views/SyncAudit/SyncRunsList.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import { Col, Container, Row, Form, Badge, Button } from "react-bootstrap";
import DynamicBreadcrumb from "../header/breadcrumb";
import Loader from "../loader/loader";
import syncAuditApi from "../../services/syncAuditApi";

const fmt = (v) => (v ? new Date(v).toLocaleString() : "-");

// datetime-local -> "YYYY-MM-DDTHH:mm"
const toApiDateTime = (dtLocal) => {
  if (!dtLocal) return null;
  return dtLocal.replace("T", " ") + ":00";
};

export default function SyncRunsList() {
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const pathArray = [
    { name: "Reports", url: "" },
    { name: "GoFrugal Sync Audit", url: "/admin/gofrugal-sync" },
  ];

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  const [status, setStatus] = useState("");
  const [fromLocal, setFromLocal] = useState("");
  const [toLocal, setToLocal] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [applied, setApplied] = useState({
    status: "",
    from: null,
    to: null,
  });

  const JOB_NAME = "gofrugal_incremental_sync";

  const normalizeRunRow = (r) => {
    if (!r) return r;
    return {
      ...r,
      job_name: r.job_name ?? r.jobName ?? r.job,
      trigger_tag: r.trigger_tag ?? r.triggerTag ?? r.trigger,
      started_at: r.started_at ?? r.startedAt ?? r.start,
      ended_at: r.ended_at ?? r.endedAt ?? r.end,
      duration_ms: r.duration_ms ?? r.durationMs ?? r.duration,
      total_branches: r.total_branches ?? r.totalBranches ?? r.branches,
      branches_success: r.branches_success ?? r.branchesSuccess ?? r.success,
      branches_failed: r.branches_failed ?? r.branchesFailed ?? r.failed,
      total_items_changed: r.total_items_changed ?? r.totalItemsChanged ?? r.items,
      total_stock_updates: r.total_stock_updates ?? r.totalStockUpdates ?? r.stock,
      total_sales_synced: r.total_sales_synced ?? r.totalSalesSynced ?? r.sales,
    };
  };

  const extractPayload = (resp) => {
    if (!resp) return null;
    if (resp.rows && typeof resp.total !== "undefined") return resp;
    if (resp.data?.rows && typeof resp.data?.total !== "undefined") return resp.data;
    if (resp.data?.data?.rows && typeof resp.data?.data?.total !== "undefined") return resp.data.data;
    if (resp.data?.data && typeof resp.data?.data === "object") return resp.data.data;
    if (resp.data && typeof resp.data === "object") return resp.data;
    return resp;
  };

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const resp = await syncAuditApi.listRuns({
        limit: paginationModel.pageSize,
        page: paginationModel.page,
        status: applied.status || null,
        from: applied.from || null,
        to: applied.to || null,
        jobName: JOB_NAME,
      });

      const payload = extractPayload(resp);
      const rawRows = Array.isArray(payload?.rows) ? payload.rows : [];
      const normalizedRows = rawRows.map(normalizeRunRow);
      const total = Number(payload?.total || 0);

      setRows(normalizedRows);
      setRowCount(total);
    } catch (err) {
      console.error(err);
      setRows([]);
      setRowCount(0);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [paginationModel.page, paginationModel.pageSize, applied.status, applied.from, applied.to]);

  useEffect(() => {
    // clear any existing interval first
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (!autoRefresh) return;

    pollRef.current = setInterval(() => {
      fetchData({ silent: true });
    }, 5000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [autoRefresh, applied.status, applied.from, applied.to, paginationModel.page, paginationModel.pageSize]);


  const columns = useMemo(
    () => [
      { field: "id", headerName: "Run ID", width: 90 },
      { field: "job_name", headerName: "Job", width: 190, renderCell: (p) => p?.row?.job_name ?? "-" },
      { field: "trigger_tag", headerName: "Trigger", width: 110, renderCell: (p) => p?.row?.trigger_tag ?? "-" },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (p) => {
          const v = p?.row?.status;
          const bg =
            v === "SUCCESS"
              ? "success"
              : v === "RUNNING"
                ? "primary"
                : v === "NO_CHANGES"
                  ? "info"
                  : v === "FAILED"
                    ? "danger"
                    : v === "SKIPPED"
                      ? "secondary"
                      : "secondary";
          return <Badge bg={bg}>{v || "-"}</Badge>;
        },
      },
      { field: "started_at", headerName: "Start", width: 190, renderCell: (p) => (p?.row?.started_at ? fmt(p.row.started_at) : "-") },
      { field: "ended_at", headerName: "End", width: 190, renderCell: (p) => (p?.row?.ended_at ? fmt(p.row.ended_at) : "-") },
      {
        field: "duration_ms",
        headerName: "Duration (s)",
        width: 130,
        renderCell: (p) => {
          const ms = p?.row?.duration_ms;
          return ms ? Math.round(ms / 1000) : "-";
        },
      },
      { field: "total_branches", headerName: "Branches", width: 110, renderCell: (p) => p?.row?.total_branches ?? 0 },
      { field: "branches_success", headerName: "Success", width: 100, renderCell: (p) => p?.row?.branches_success ?? 0 },
      { field: "branches_failed", headerName: "Failed", width: 90, renderCell: (p) => p?.row?.branches_failed ?? 0 },
      { field: "total_items_changed", headerName: "Items", width: 90, renderCell: (p) => p?.row?.total_items_changed ?? 0 },
      { field: "total_stock_updates", headerName: "Stock", width: 90, renderCell: (p) => p?.row?.total_stock_updates ?? 0 },
      { field: "total_sales_synced", headerName: "Sales", width: 90, renderCell: (p) => p?.row?.total_sales_synced ?? 0 },
    ],
    []
  );

  const onApply = () => {
    setAutoRefresh(false);
    setPaginationModel((p) => ({ ...p, page: 0 }));
    setApplied({
      status,
      from: toApiDateTime(fromLocal),
      to: toApiDateTime(toLocal),
    });
  };

  const onClear = () => {
    setAutoRefresh(false);
    setStatus("");
    setFromLocal("");
    setToLocal("");
    setPaginationModel((p) => ({ ...p, page: 0 }));
    setApplied({
      status: "",
      from: null,
      to: null,
    });
  };

  return (
    <Container className="mt-4 mb-4">

      <div className="d-flex  mb-2">
        {/* Left */}
        <div className="flex-grow-1 ">
          <DynamicBreadcrumb pathArray={pathArray} />
        </div>

        {/* Right */}
        <div className="d-flex align-items-center gap-2 flex-grow-1 text-end">
          <Form.Check
            type="switch"
            id="sync-auto-refresh"
            label="Auto Refresh"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="mb-0"
          />

          <Button
            size="sm"
            className="px-3"
            variant="primary"
            onClick={() => fetchData()}
          >
            Refresh
          </Button>

          <Button
            size="sm"
            className="px-3"
            variant="success"
            onClick={() => navigate("/admin/gofrugal-sync/unmatched")}
          >
            Unmatched Items
          </Button>
        </div>
      </div>

      <div className="d-flex align-items-center mb-3">
        <h5 className="mb-0">GoFrugal Sync Audit</h5>
      </div>

      {loading && <Loader />}

      <Button className="mb-3" variant="outline-primary" onClick={() => navigate('/admin/gofrugal-sync/item-check')}>Item Sync Check</Button>
      <Button className="mb-3 ms-2" variant="outline-primary" onClick={() => navigate('/admin/gofrugal-offline')}>Offline delivery sync</Button>

      <Row className="mb-3 align-items-end">
        <Col md={2}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="RUNNING">RUNNING</option>
              <option value="SUCCESS">SUCCESS</option>
              <option value="NO_CHANGES">NO_CHANGES</option>
              <option value="FAILED">FAILED</option>
              <option value="SKIPPED">SKIPPED</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>From</Form.Label>
            <Form.Control type="datetime-local" value={fromLocal} onChange={(e) => setFromLocal(e.target.value)} />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>To</Form.Label>
            <Form.Control type="datetime-local" value={toLocal} onChange={(e) => setToLocal(e.target.value)} />
          </Form.Group>
        </Col>

        <Col md={4} className="d-flex gap-2">
          <Button size="sm" className="px-3" variant="primary" onClick={onApply}>
            Apply
          </Button>

          <Button
            size="sm"
            className="px-3"
            variant="secondary"
            onClick={onClear}
          >
            Clear
          </Button>
        </Col>
      </Row>

      <Box>
        <DataGrid
          rows={Array.isArray(rows) ? rows : []}
          columns={columns}
          getRowId={(r) => r.id}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginationMode="server"
          rowCount={rowCount}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          onRowDoubleClick={(params) => navigate(`/admin/gofrugal-sync/${params.row.id}`)}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: false },
            },
          }}
          sx={{ [`& .${gridClasses.cell}`]: { py: 0.5 } }}
        />
      </Box>

      <div className="text-muted mt-2" style={{ fontSize: 12 }}>
        Tip: Double-click a run row to open branch-wise details.
      </div>
    </Container>
  );
}

